import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Scale,
  Award,
  Microscope,
  MessageCircle,
} from "lucide-react";
import { WHATSAPP_KATIA, WHATSAPP_GERARDO, WHATSAPP_URL } from "@/lib/brand";

const specialists = [
  {
    name: "Mtra. Katia Marcin Olascoaga",
    role: "Psicóloga Clínica y Forense · Fundadora",
    photo:
      "https://customer-assets.emergentagent.com/job_web-page-23/artifacts/njz148f5_ChatGPT%20Image%2029%20jun%202026%2C%2012_03_08%20p.m..png",
    whatsapp: WHATSAPP_KATIA,
    intro:
      "Fundadora de Psychelibrium. Psicóloga con maestría en Psicología Clínica y Forense, dedicada a la práctica clínica, la investigación y el peritaje forense.",
    paragraphs: [
      "Su trabajo se centra en procesos terapéuticos integradores para adolescentes, adultos, parejas y familias, con especial atención a la comunidad LGBTQ+.",
      "En el ámbito legal, se desempeña como perito forense elaborando dictámenes y metaperitajes con rigor metodológico e independencia científica.",
    ],
    tags: ["TCC", "TDC", "Sistémica", "Mindfulness", "Forense", "Investigación"],
    credentials: [
      { Icon: GraduationCap, text: "Maestría en Psicología Clínica y Forense · Cédula en trámite" },
      { Icon: GraduationCap, text: "Doctorante en Psicología Clínica y Forense" },
      { Icon: Microscope, text: "Investigadora y perito forense" },
      { Icon: Award, text: "CONAPPSIM · Mesa directiva, Vocal 1" },
    ],
  },
  {
    name: "Mtro. Gerardo Abadía León",
    role: "Psicólogo Clínico y Forense · Cofundador",
    photo:
      "https://customer-assets.emergentagent.com/job_web-page-23/artifacts/wwgfd376_Gemini_Generated_Image_h0da8vh0da8vh0da.png",
    whatsapp: WHATSAPP_GERARDO,
    intro:
      "Cofundador de Psychelibrium. Psicólogo con maestría en Psicología Clínica y Forense, investigador y perito forense con formación integradora.",
    paragraphs: [
      "Acompaña procesos individuales y de pareja desde una mirada humanista y gestáltica, con herramientas cognitivo-conductuales y sistémicas.",
      "Como perito forense participa en la elaboración de dictámenes y metaperitajes, con estricto apego ético y científico dentro del sistema de justicia.",
    ],
    tags: ["TCC", "Gestalt", "Humanista", "Sistémica", "Forense", "Investigación"],
    credentials: [
      { Icon: GraduationCap, text: "Maestría en Psicología Clínica y Forense · Cédula en trámite" },
      { Icon: GraduationCap, text: "Doctorante en Psicología Clínica y Forense" },
      { Icon: Microscope, text: "Investigador y perito forense" },
      { Icon: Award, text: "CONAPPSIM · Mesa directiva, Vocal 2" },
    ],
  },
];

const Especialistas = () => {
  return (
    <div data-testid="especialistas-page">
      <PageHeader
        eyebrow="03 · Especialistas"
        title="Dos psicólogos, un mismo compromiso."
        subtitle="Formación de maestría, investigación activa y práctica clínica y forense sostenida por la ética profesional."
      />

      <section className="px-6 md:px-14 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {specialists.map((s, i) => (
          <article
            key={s.name}
            data-testid={`specialist-${i}`}
            className="card-soft overflow-hidden rise"
            style={{ animationDelay: `${140 * (i + 1)}ms` }}
          >
            <div className="aspect-[4/3] overflow-hidden relative">
            <img
                src={s.photo}
                alt={s.name}
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 55%, rgba(74,21,33,0.35) 100%)",
                }}
              />
              <div className="absolute bottom-4 left-4">
                <span
                  className="px-3 py-1 rounded-full text-[11px]"
                  style={{
                    background: "rgba(245,239,230,0.92)",
                    color: "var(--p-burgundy)",
                    fontFamily: "Cinzel, serif",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  {i === 0 ? "Fundadora" : "Cofundador"}
                </span>
              </div>
            </div>
            <div className="p-8">
              <p className="eyebrow" style={{ color: "var(--p-burgundy)" }}>
                {s.role}
              </p>
              <h3
                className="mt-2"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "1.85rem",
                  fontWeight: 600,
                  color: "var(--p-black)",
                  letterSpacing: "-0.005em",
                  lineHeight: 1.15,
                }}
              >
                {s.name}
              </h3>

              <p
                className="mt-4"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  color: "var(--p-black)",
                  fontSize: "1.08rem",
                  lineHeight: 1.6,
                }}
              >
                {s.intro}
              </p>

              {s.paragraphs.map((p, idx) => (
                <p
                  key={idx}
                  className="mt-3"
                  style={{
                    fontFamily: "Cormorant Garamond, serif",
                    color: "var(--p-brown)",
                    fontSize: "1.02rem",
                    lineHeight: 1.6,
                  }}
                >
                  {p}
                </p>
              ))}

              {/* Credentials */}
              <div className="mt-6 space-y-2">
                {s.credentials.map((c, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3"
                    data-testid={`credential-${i}-${idx}`}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                      style={{
                        background: "rgba(184,151,90,0.15)",
                        color: "var(--p-burgundy)",
                      }}
                    >
                      <c.Icon size={14} strokeWidth={1.5} />
                    </div>
                    <span
                      style={{
                        fontFamily: "Cormorant Garamond, serif",
                        color: "var(--p-black)",
                        fontSize: "1rem",
                      }}
                    >
                      {c.text}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1 rounded-full text-xs"
                    style={{
                      background: "rgba(184,151,90,0.15)",
                      color: "var(--p-burgundy)",
                      fontFamily: "Cinzel, serif",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={s.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`specialist-${i}-agendar`}
                  className="btn-primary"
                >
                  <MessageCircle size={16} />
                  Agendar por WhatsApp
                </a>
                <Link
                  to="/contacto"
                  data-testid={`specialist-${i}-contacto`}
                  className="btn-ghost"
                >
                  Enviar mensaje
                </Link>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="px-6 md:px-14 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Credential
            Icon={GraduationCap}
            title="Maestría"
            text="Formación de posgrado en psicología clínica y forense. (Cédula en trámite)"
          />
          <Credential
            Icon={Scale}
            title="Ética profesional"
            text="Práctica alineada al código deontológico y estándares internacionales."
          />
        </div>
        <div className="mt-10">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="especialistas-cta"
            className="btn-primary"
          >
            Agenda con un especialista
          </a>
        </div>
      </section>
    </div>
  );
};

const Credential = ({ Icon, title, text }) => (
  <div className="p-6 card-soft" style={{ background: "#fbf6ec" }}>
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center"
      style={{ background: "rgba(74,21,33,0.06)", color: "var(--p-burgundy)" }}
    >
      <Icon size={18} strokeWidth={1.4} />
    </div>
    <h3 className="mt-4 font-deco text-base" style={{ color: "var(--p-burgundy)" }}>
      {title}
    </h3>
    <p
      className="mt-2"
      style={{
        fontFamily: "Cormorant Garamond, serif",
        color: "var(--p-brown)",
        lineHeight: 1.55,
      }}
    >
      {text}
    </p>
  </div>
);

export default Especialistas;
